import { Formik, FormikHelpers } from 'formik';
import get from 'lodash.get';
import React from 'react';
import Form from 'react-bootstrap/Form';
import { AiOutlineUser } from 'react-icons/ai';
import { IoIosCamera } from 'react-icons/io';
import { Link, RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { object, string } from 'yup';

import apiUtil from 'src/api/userManagement';
import CustomToast from 'src/components/CustomToast';
import FormikSubmit from 'src/components/FormikSubmit';
import ImgUpload from 'src/components/ImgUpload';
import { TermsConditions } from 'src/components/TermsConditions';
import { sentCodeSuccess } from 'src/containers/Core/Messages';
import { SIGN_IN, VERIFY_EMAIL } from 'src/containers/Core/Paths';
import { emailSchema, newPasswordSchema, notEmptySchema } from 'src/validationSchemas';

type FormValues = {
  name: string;
  profilePicture?: string;
  phoneNumber: string;
  email: string;
  password: string;
};

type SignUpFormProps = {
  onSubmit: FormikOnSubmit<FormValues>;
  businessName?: string;
};

const SignUp: React.FunctionComponent<RouteComponentProps<{}, {}, Partial<{ email: string }>>> = ({ history }) => {
  // tslint:disable-next-line:no-any
  const onSubmit = async (values: any, actions: FormikHelpers<FormValues>) => {
    try {
      const { email } = values;
      const formData = new FormData();

      Object.keys(values).forEach((key: string) => {
        formData.append(key, values[key]);
      });

      if (!values.profilePicture) {
        formData.delete('profilePicture');
      }

      formData.append('persona', 'business');

      await apiUtil.signUp(formData);
      toast(<CustomToast type="success" description={sentCodeSuccess(email)} />, { className: 'success' });
      actions.resetForm();

      return history.push(VERIFY_EMAIL, { email });
    } catch (errors) {
      if (errors.response.status === 400) {
        actions.setErrors(get(errors, 'response.data'));
      } else {
        toast(<CustomToast description={get(errors, 'response.data.error')} />);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <>
      <h2 className={`mb-3 font-weight-bold text-primary mt-2`}>Add your business to Open Rewards</h2>
      <SignUpForm onSubmit={onSubmit} />
    </>
  );
};

// TODO: Move the MSP, MDP and connect functions everywhere to bottom of the files
export default SignUp;

// TODO: Move such (unscoped) objects inside a component/clojure everywhere to improve performance
const schema = object({
  email: emailSchema,
  phoneNumber: string().max(50, 'Phone number can be a maximum of 50 characters.'),
  password: newPasswordSchema.password,
  name: notEmptySchema.max(255, 'Name can be a maximum of 255 characters.'),
});

const PlaceHolderImage = (
  <div
    className={`profile-placeholder d-flex align-items-center justify-content-center rounded-circle
    position-relative p-3 bg-light c-pointer`}
  >
    <span>
      <AiOutlineUser size={24} className="royal-blue-color" />
    </span>
    <span
      className="d-inline-block position-absolute p-1 bg-white rounded-circle text-muted"
      style={{
        bottom: 0,
        right: 0,
        lineHeight: 0.5,
      }}
    >
      <IoIosCamera size={18} />
    </span>
  </div>
);

const SignUpForm: React.FunctionComponent<SignUpFormProps> = ({ onSubmit }) => (
  <Formik
    validationSchema={schema}
    onSubmit={onSubmit}
    initialValues={{
      name: '',
      profilePicture: null,
      phoneNumber: '',
      email: '',
      password: '',
    }}
    enableReinitialize
  >
    {({ errors, handleChange, setFieldValue, values, handleSubmit, touched, isSubmitting }) => (
      <Form noValidate onSubmit={handleSubmit}>
        <Form.Group className="d-flex flex-column align-items-center justify-content-center mb-1">
          <ImgUpload
            className="align-items-center"
            onSubmitCallback={(data: FormData) => {
              setFieldValue('profilePicture', data.get('profilePicture'));
            }}
            customPlaceholder={PlaceHolderImage}
            img={values.profilePicture}
            objkey="profilePicture"
          />
          <Form.Label className="text-muted">Profile Picture</Form.Label>
        </Form.Group>
        <Form.Group controlId="Name">
          <Form.Label className="required">Name</Form.Label>
          <Form.Control
            type="text"
            name="name"
            placeholder="Enter name"
            value={values.name}
            onChange={handleChange}
            isInvalid={touched.name && !!errors.name}
            autoComplete="off"
            disabled={isSubmitting}
            maxLength={255}
          />
          <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group controlId="PhoneNumber">
          <Form.Label>Mobile Number</Form.Label>
          <Form.Control
            type="text"
            name="phoneNumber"
            placeholder="Enter mobile number"
            value={values.phoneNumber}
            onChange={handleChange}
            isInvalid={touched.phoneNumber && !!errors.phoneNumber}
            autoComplete="off"
            disabled={isSubmitting}
            maxLength={255}
          />
          <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group controlId="Email">
          <Form.Label className="required">Email Address</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email address"
            name="email"
            value={values.email}
            onChange={handleChange}
            disabled={isSubmitting}
            isInvalid={touched.email && !!errors.email}
            autoComplete="off"
            maxLength={255}
          />
          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group controlId="Password">
          <Form.Label className="required">Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Enter password"
            name="password"
            value={values.password}
            onChange={handleChange}
            isInvalid={touched.password && !!errors.password}
            autoComplete="new-password"
            disabled={isSubmitting}
            maxLength={255}
          />
          <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
        </Form.Group>

        <TermsConditions />
        <FormikSubmit
          variant="primary"
          block
          className="font-weight-bold mt-3"
          loading={isSubmitting}
          text="Create Account"
        />

        <p className={`text-center mt-3`}>
          Already have an account?
          <Link to={SIGN_IN} className={`ml-1 font-weight-bold text-decoration-none`}>
            Sign In
          </Link>
        </p>
      </Form>
    )}
  </Formik>
);
